import { Card, CardContent, } from "@/components/ui/card";
import "./index.css";
import { defaultCharacter, type Character } from "./lib/model";
import { useCallback, useEffect, useState } from "react";
import { Container } from "./components/Container";
import { Stat } from "./components/Stat";
import { marked } from "marked";

export function App() {
	const [character, setCharacter] = useState(defaultCharacter);
	const [notesEditing, setNotesEditing] = useState(false);
	const [init, setInit] = useState(false);

	useEffect(() => {
		if (init) return;
		setInit(true);
		if (localStorage.getItem("character")) {
			setCharacter(JSON.parse(localStorage.getItem("character")));
		} else if (document.cookie.split(";").some(s => s.trim().startsWith("character="))) {
			const cookie = document.cookie.split(";").find(s => s.trim().startsWith("character="))
			const [_v, k] = cookie.split("=");
			setCharacter(JSON.parse(k));
		}
	}, [])

	const persist = (newChar: Character) => {
		const str = JSON.stringify(newChar);
		localStorage.setItem("character", str);
		document.cookie = `character=${str}; expires=Fri, 31 Dec 9999 23:59:59 GMT; SameSite=None; Secure`
	};

	const update = (key: keyof Character, newVal: any) => {
		const newChar = { ...character, [key]: newVal };
		persist(newChar)
		setCharacter(newChar);
	};

	const updateTable = (key: "skills" | "items", idx: number, newVal: string) => {
		const newArr = [...character[key]];
		newArr[idx] = newVal;
		const newChar = { ...character, [key]: newArr };
		setCharacter(newChar);
		persist(newChar);
	};

	const updateBody = (bodyPart: keyof Character["body"], stat: keyof Character["body"][typeof bodyPart], newVal: string) => {
		const newBody = { ...character.body };
		newBody[bodyPart][stat] = newVal;
		const newChar = { ...character, body: newBody };
		persist(newChar);
		setCharacter(newChar);
	};

	return (
		<div className="w-screen flex flex-col items-center max-w-screen gap-4">
			<h1 className="text-3xl font-serif text-base font-bold italic">Surviving Winter</h1>
			<Card>
				<CardContent>
					<div className="flex flex-row gap-4 my-6 text-base font-bold italic">
						<Container>
							<h2 className="text-xl">Identyfikator Łowcy</h2>
							<Stat stat={character.origin} display="Pochodzenie" update={newVal => update('origin', newVal)} />
							<Stat stat={character.group} display="Grupa" update={newVal => update('group', newVal)} />
							<Stat stat={character.name} display="Imię" update={newVal => update('name', newVal)} />
							<Stat stat={character.nick} display="Kryptonim" update={newVal => update('nick', newVal)} />
							<Stat stat={character.unit} display="Jednostka" update={newVal => update('unit', newVal)} />
						</Container>
						<Container>
							<h2 className="text-xl">Statystyki</h2>
							<Stat stat={character.str} width="50" display="Siła" update={newVal => update('str', newVal)} />
							<Stat stat={character.dex} width="50" display="Zręczność" update={newVal => update('dex', newVal)} />
							<Stat stat={character.con} width="50" display="Tężyzna" update={newVal => update('con', newVal)} />
							<Stat stat={character.per} width="50" display="Percepcja" update={newVal => update('per', newVal)} />
							<Stat stat={character.tough} width="50" display="Wytrzymałość" update={newVal => update('tough', newVal)} />
							<Stat stat={character.edu} width="50" display="Wykształcenie" update={newVal => update('edu', newVal)} />
						</Container>
						<Container>
							<h2 className="text-xl">Stan Ogólny</h2>
							<Stat stat={character.health} width="50" display="Zdrowie" update={newVal => update('health', newVal)} />
							<Stat stat={character.morale} width="50" display="Morale" update={newVal => update('morale', newVal)} />
							<Stat stat={character.will} width="50" display="Wola" update={newVal => update('will', newVal)} />
							<Stat stat={character.zero} width="50" display="-Zero" update={newVal => update('zero', newVal)} />
						</Container>
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardContent>
					<div className="flex flex-col gap-4 my-6 text-base font-bold italic">
						<h2 className="text-xl">Specjalizacje</h2>
						<div className="flex flex-row gap-4 my-6">
							<div className="flex flex-col gap-2">
								<Stat stat={character.acrobatics} default="5" width="50" display="Akrobatyka" update={newVal => update('acrobatics', newVal)} />
								<Stat stat={character.athletics} default="5" width="50" display="Atletyka" update={newVal => update('athletics', newVal)} />
								<Stat stat={character.archeology} default="5" width="50" display="Archeologia" update={newVal => update('archeology', newVal)} />
								<Stat stat={character.firearms} default="5" width="50" display="Broń Palna" update={newVal => update('firearms', newVal)} />
								<Stat stat={character.eye} default="5" width="50" display="Czujne Oko" update={newVal => update('eye', newVal)} />
								<Stat stat={character.electric} default="5" width="50" display="Elektryka" update={newVal => update('electric', newVal)} />
								<Stat stat={character.history} default="5" width="50" display="Historia" update={newVal => update('history', newVal)} />
								<Stat stat={character.intuition} default="5" width="50" display="Intuicja" update={newVal => update('intuition', newVal)} />
							</div>
							<div className="flex flex-col gap-2">
								<Stat stat={character.pockerface} default="5" width="50" display="Kammiena Twarz" update={newVal => update('pockerface', newVal)} />
								<Stat stat={character.breathcontrol} default="5" width="50" display="Kontrola Oddechu" update={newVal => update('breathcontrol', newVal)} />
								<Stat stat={character.technique} default="5" width="50" display="Technika" update={newVal => update('technique', newVal)} />
								<Stat stat={character.mechanic} default="5" width="50" display="Mechanika" update={newVal => update('mechanic', newVal)} />
								<Stat stat={character.medicine} default="5" width="50" display="Medycyna" update={newVal => update('medicine', newVal)} />
								<Stat stat={character.nature} default="5" width="50" display="Natura" update={newVal => update('nature', newVal)} />
								<Stat stat={character.navigation} default="5" width="50" display="Nawigacja" update={newVal => update('navigation', newVal)} />
								<Stat stat={character.woundtending} default="5" width="50" display="Opatrywanie Ran" update={newVal => update('woundtending', newVal)} />
							</div>
							<div className="flex flex-col gap-2">
								<Stat stat={character.persuasion} default="5" width="50" display="Perswazja" update={newVal => update('persuasion', newVal)} />
								<Stat stat={character.leadership} default="5" width="50" display="Przywództwo" update={newVal => update('leadership', newVal)} />
								<Stat stat={character.psychology} default="5" width="50" display="Psychologia" update={newVal => update('psychology', newVal)} />
								<Stat stat={character.crafting} default="5" width="50" display="Rzemiosło" update={newVal => update('crafting', newVal)} />
								<Stat stat={character.investigate} default="5" width="50" display="Przeszukiwanie" update={newVal => update('investigate', newVal)} />
								<Stat stat={character.survival} default="5" width="50" display="Przetrwanie" update={newVal => update('survival', newVal)} />
								<Stat stat={character.hiding} default="5" width="50" display="Ukrywanie" update={newVal => update('hiding', newVal)} />
								<Stat stat={character.melee} default="5" width="50" display="Walka Wręcz" update={newVal => update('melee', newVal)} />
							</div>
							<div className="flex flex-col gap-2">
								<Container>
									<Stat stat={character.luck} width="50" display="Szczęście" update={newVal => update('luck', newVal)} />
									<Stat stat={character.expertise} width="50" display="Ekspertyza" update={newVal => update('expertise', newVal)} />
								</Container>
								<Container>
									<Stat stat={character.luckPoints} width="50" display="Punkty Szczęścia" update={newVal => update('luckPoints', newVal)} />
									<Stat stat={character.expertisePoints} width="50" display="Punkty Eksperyzy" update={newVal => update('expertisePoints', newVal)} />
								</Container>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardContent>
					<div className="flex flex-row gap-4 my-6 text-base font-bold italic">
						<Container>
							<h2 className="text-xl">Umiejętności</h2>
							<div className="flex flex-col gap-2 my-6 max-h-[470px] overflow-y-auto">
								{character.skills.map((skill, idx) => <div key={idx} className="flex flex-row p-1 gap-2">
									<label>{idx + 1}</label>
									<input width="50" className="border-b-1" onBlur={e => updateTable('skills', idx, e.target.value)} />
								</div>
								)}
							</div>
						</Container>
						<Container>
							<h2 className="text-xl">Ekwipunek</h2>
							<div className="flex flex-col gap-2 my-6 max-h-[470px] overflow-y-auto">
								{character.items.map((item, idx) => <div key={idx} className="flex flex-row p-1 gap-2">
									{
										idx !== 10 ?
											<>
												<label>{idx < 11 ? idx + 1 : idx}</label>
												<input width="50" className="border-b-1" defaultValue={item} onBlur={e => updateTable('items', idx, e.target.value)} />
											</>
											: <div className="border-b-3 border-dashed w-full h-[25px]"></div>
									}
								</div>
								)}
							</div>
						</Container>
						<div>
							<Container>
								<h2 className="text-xl">Pancerz</h2>
								<table className="table-auto w-full border-collapse">
									<thead>
										<tr>
											<th></th>
											<th>P</th>
											<th>T</th>
											<th>W</th>
										</tr>
									</thead>
									<tbody>
										{Object.keys(character.body).map((bodyPart: keyof Character["body"], idx) => <tr key={idx} className="odd:bg-gray-100">
											<td className="border-b-2 border-gray-200 p-1 capitalize">{bodyPart}</td>
											<td className="border-b-2 border-gray-200 p-1">
												<input type="text" className="border-b-1 w-[25px]" defaultValue={character.body[bodyPart].p} onBlur={e => updateBody(bodyPart, "p", e.target.value)} />
											</td>
											<td className="border-b-2 border-gray-200 p-1">
												<input type="text" className="border-b-1 w-[25px]" defaultValue={character.body[bodyPart].t} onBlur={e => updateBody(bodyPart, "t", e.target.value)} />
											</td>
											<td className="border-b-2 border-gray-200 p-1">
												<input type="text" className="border-b-1 w-[25px]" defaultValue={character.body[bodyPart].w} onBlur={e => updateBody(bodyPart, "w", e.target.value)} />
											</td>
										</tr>
										)}
									</tbody>
								</table>
							</Container>
						</div>
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardContent style={{ fontStyle: "unset", fontWeight: "unset", fontSize: "unset", color: "unset" }}>
					<h1 className="text-3xl text-base font-bold italic">Notes</h1>
					{
						notesEditing ? <textarea autoFocus className="border-b-1" cols={80} rows={30} defaultValue={character.notes ?? ""} onBlur={e => { update("notes", e.target.value); setNotesEditing(false) }} /> : <div className="notes w-[900px] min-h-75" onClick={() => setNotesEditing(true)} dangerouslySetInnerHTML={{ __html: marked.parse(character.notes ?? "") }}></div>
					}
				</CardContent>
			</Card>
		</div>
	);
}

export default App;
